"use client"
import { useState } from "react"
import { Content, isFilled } from "@prismicio/client"
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next"
import { SliceComponentProps } from "@prismicio/react"
import { PlayIcon } from "@radix-ui/react-icons"
import Button from "@aura-design/system/button"
import Section from "@aura-design/system/section"
import EmptyAlert from "@airplan/ui/empty-alert"

import { modifyEmbedString } from "@airplan/utils/data-view"

/**
 * Props for `FeatureVideo`.
 */
export type FeatureVideoProps = SliceComponentProps<Content.FeatureVideoSlice>

/**
 * Component for "FeatureVideo" Slices.
 */
const FeatureVideo = ({ slice }: FeatureVideoProps): JSX.Element => {
  const [isPlaying, setIsPlaying] = useState(false)
  const isYoutube = slice.primary?.video?.provider_name === "YouTube"

  if (!isYoutube) {
    return (
      <Section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="disabled"
        subClassName="valign vfluid"
      >
        <EmptyAlert
          title="Ooops"
          description="No se ha encontrado ningún video de YouTube."
        />
      </Section>
    )
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative h-[100vh]"
    >
      {isPlaying ? (
        <>
          {isFilled.embed(slice.primary.video) && (
            <div
              className="embed-yt-video"
              dangerouslySetInnerHTML={{
                __html: modifyEmbedString(slice.primary.video.html as string),
              }}
            />
          )}
        </>
      ) : (
        <>
          {isFilled.image(slice.primary.cover) &&
            !isFilled.image(slice.primary.cover_mobile) && (
              <>
                <PrismicNextImage
                  field={slice.primary.cover}
                  className="hide-small object-cover"
                  imgixParams={{
                    txt: slice.primary.cover?.copyright,
                  }}
                  fill
                  fallbackAlt=""
                />
                <PrismicNextImage
                  field={slice.primary.cover}
                  className="hide-medium hide-large object-cover"
                  imgixParams={{
                    width: 768,
                    ar: "9:16",
                    crop: ["left"],
                    fit: "crop",
                    txt: slice.primary.cover?.copyright,
                  }}
                  fill
                  fallbackAlt=""
                />
              </>
            )}
            {isFilled.image(slice.primary.cover) &&
            isFilled.image(slice.primary.cover_mobile) && (
              <>
                <PrismicNextImage
                  field={slice.primary.cover}
                  className="hide-small object-cover"
                  imgixParams={{
                    txt: slice.primary.cover?.copyright,
                  }}
                  fill
                  fallbackAlt=""
                />
                <PrismicNextImage
                  field={slice.primary.cover_mobile}
                  className="hide-medium hide-large object-cover"
                  imgixParams={{
                    width: 768,
                    ar: "9:16",
                    crop: ["left"],
                    fit: "crop",
                    txt: slice.primary.cover?.copyright,
                  }}
                  fill
                  fallbackAlt=""
                />
              </>
            )}
          <div className="absolute left-0 top-0 h-full w-full bg-dark-blue opacity-40" />
          <div className="action">
            <div className="valign circle h-4 w-4 justify-center bg-light-gray">
              <Button
                mode="link"
                className="circle p-0"
                onClick={() => setIsPlaying(true)}
              >
                <PlayIcon className="icon h4 text-stale-gray" />
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

export default FeatureVideo
